import { useRef, useState } from 'react';
import { Icon } from './icons/Icon';
import { Modal, Button, useToast } from './primitives';
import {
  useLogos,
  useActiveLogo,
  useCreateLogo,
  useActivateLogo,
  useDeleteLogo,
  useDeactivateLogo,
} from '../api/hooks';
import styles from './LogoLibraryButton.module.css';

const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Knihovna log firmy — nahrání, výběr aktivního a mazání. Aktivní logo se
 * automaticky propíše do hlavičky všech dokumentů (DocPage) i do PDF exportu.
 * Port chování ze staré verze (frontend/index.html, sekce „Logo firmy").
 */
export function LogoLibraryButton() {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const { data: logos = [], isLoading } = useLogos();
  const { data: active } = useActiveLogo();
  const createLogo = useCreateLogo();
  const activateLogo = useActivateLogo();
  const deleteLogo = useDeleteLogo();
  const deactivateLogo = useDeactivateLogo();

  const busy = createLogo.isPending || activateLogo.isPending;

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // umožní znovu vybrat stejný soubor
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Vyber prosím obrázek (PNG, JPG, SVG, WEBP).');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Logo je příliš velké (max 2 MB).');
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      const name = file.name.replace(/\.[^.]+$/, '') || 'Logo';
      const created = await createLogo.mutateAsync({ name, data_url: dataUrl });
      await activateLogo.mutateAsync(created.id);
      toast.success('Logo nahráno a nastaveno jako aktivní.');
    } catch {
      toast.error('Nahrání loga selhalo.');
    }
  }

  async function handleActivate(id: number) {
    try {
      await activateLogo.mutateAsync(id);
      toast.success('Logo nastaveno jako aktivní.');
    } catch {
      toast.error('Nepodařilo se aktivovat logo.');
    }
  }

  async function handleDelete(id: number) {
    try {
      await deleteLogo.mutateAsync(id);
      toast.success('Logo smazáno.');
    } catch {
      toast.error('Nepodařilo se smazat logo.');
    }
  }

  async function handleDeactivate() {
    try {
      await deactivateLogo.mutateAsync();
      toast.success('Žádné logo není aktivní — dokumenty použijí iniciály.');
    } catch {
      toast.error('Akce selhala.');
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        leading={<Icon name="letterhead" />}
        onClick={() => setOpen(true)}
        title="Logo firmy na dokumentech"
      >
        <span className="sd-phone-hide">Logo</span>
      </Button>

      <Modal open={open} onClose={() => setOpen(false)} title="📚 Knihovna log" size="md">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFile}
        />

        <button
          type="button"
          className={styles.dropArea}
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          <span className={styles.dropIcon} aria-hidden>🖼️</span>
          <span className={styles.dropTitle}>{busy ? 'Nahrávám…' : 'Klikni pro nahrání loga'}</span>
          <span className={styles.dropHint}>PNG, JPG, SVG, WEBP — zobrazí se v hlavičce dokumentů a v PDF</span>
        </button>

        <div className={styles.listHead}>
          <span>Uložená loga</span>
          {active && (
            <button type="button" className={styles.linkBtn} onClick={handleDeactivate}>
              Zrušit aktivní
            </button>
          )}
        </div>

        {isLoading ? (
          <div className={styles.empty}>Načítám…</div>
        ) : logos.length === 0 ? (
          <div className={styles.empty}>Zatím žádná uložená loga.</div>
        ) : (
          <ul className={styles.list}>
            {logos.map((logo) => {
              const isActive = active?.id === logo.id;
              return (
                <li key={logo.id} className={styles.item}>
                  <div className={styles.thumb}>
                    <img src={logo.data_url} alt={logo.name} />
                  </div>
                  <div className={styles.itemMeta}>
                    <span className={styles.itemName}>{logo.name}</span>
                    {isActive && <span className={styles.activeBadge}>● aktivní</span>}
                  </div>
                  <div className={styles.itemActions}>
                    {!isActive && (
                      <Button size="sm" variant="secondary" onClick={() => handleActivate(logo.id)}>
                        Použít
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(logo.id)}
                      title="Smazat logo"
                    >
                      ✕
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Modal>
    </>
  );
}
